import React, { useRef, useState } from 'react';

// Import a test suite from a JSON file into the Test Suite Catalog.
// Accepts {suite_id, version, prompts:[{id, prompt, expected_output, ...}]} or a bare list.
// Validates on the server first (dry run) and shows a preview before writing.
export default function SuiteImport({ headers, onImported }) {
  const fileRef = useRef(null);
  const [payload, setPayload] = useState(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const post = async (body) => {
    const r = await fetch('/api/evaluate', { method: 'POST', headers: headers(), body: JSON.stringify(body) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
    return d;
  };

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    setMsg(''); setPreview(null); setPayload(null); setFileName(f.name);
    let json;
    try { json = JSON.parse(await f.text()); } catch (err) { setMsg(`Not valid JSON: ${err.message}`); return; }
    const suite = Array.isArray(json) ? { prompts: json } : json;
    const rows = suite.prompts || [];
    if (!Array.isArray(rows) || !rows.length) { setMsg("JSON needs a non-empty 'prompts' list."); return; }
    setBusy(true);
    try {
      const d = await post({ action: 'import_suite', suite, dry_run: true });
      setPayload(suite);
      setPreview({ ...d, rows, suite_id: suite.suite_id || 'Imported_Suite', version: suite.version || '1.0' });
    } catch (err) { setMsg(`Check failed: ${err.message}`); }
    setBusy(false);
  };

  const doImport = async () => {
    setBusy(true); setMsg('');
    try {
      const d = await post({ action: 'import_suite', suite: payload });
      setMsg(`✅ ${d.message}`); setPreview(null); setPayload(null);
      if (onImported) onImported(d);
    } catch (err) { setMsg(`Import failed: ${err.message}`); }
    setBusy(false);
  };

  const countBy = (k) => {
    const c = {};
    (preview ? preview.rows : []).forEach(r => { const v = r[k] || '—'; c[v] = (c[v] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  };
  const btn = (bg) => ({ padding: '8px 16px', backgroundColor: bg, color: '#fff', border: 'none', borderRadius: '4px', fontWeight: 'bold', cursor: busy ? 'default' : 'pointer', fontSize: '13px', opacity: busy ? 0.7 : 1 });

  return (
    <>
      <input ref={fileRef} type="file" accept=".json,application/json" onChange={onFile} style={{ display: 'none' }} />
      <button onClick={() => fileRef.current && fileRef.current.click()} disabled={busy} style={btn('#6f42c1')}>
        {busy && !preview ? 'Checking…' : '📤 Import JSON'}
      </button>
      {(preview || msg) && (
        <div style={{ flexBasis: '100%', backgroundColor: '#fff', border: '2px solid #6f42c1', borderRadius: '8px', padding: '14px', marginTop: '10px' }}>
          {msg && <div style={{ fontSize: '13px', marginBottom: preview ? '8px' : 0, color: msg.startsWith('✅') ? '#155724' : '#b00020' }}>{msg}</div>}
          {preview && (
            <>
              <div style={{ fontSize: '13px', marginBottom: '6px' }}>
                <b>{fileName}</b> → suite <b>{preview.suite_id}</b> v{preview.version}: <b>{preview.valid}</b> prompts ready
                {preview.skipped ? <span style={{ color: '#b7791f' }}> · {preview.skipped} will be skipped</span> : null}
              </div>
              <div style={{ display: 'flex', gap: '24px', fontSize: '12px', color: '#4a5568', flexWrap: 'wrap', marginBottom: '6px' }}>
                {['category', 'kpi'].map(k => (
                  <div key={k}><b>By {k}:</b> {countBy(k).map(([v, n]) => `${v} (${n})`).join(', ')}</div>
                ))}
              </div>
              {preview.errors && preview.errors.length > 0 && (
                <div style={{ fontSize: '11px', color: '#b7791f', marginBottom: '6px' }}>Skipped: {preview.errors.join('; ')}</div>
              )}
              <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #edf2f7', borderRadius: '4px', marginBottom: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                  <thead><tr style={{ background: '#f7fafc', textAlign: 'left' }}><th style={{ padding: '4px' }}>ID</th><th style={{ padding: '4px' }}>Category</th><th style={{ padding: '4px' }}>Prompt</th></tr></thead>
                  <tbody>{preview.rows.slice(0, 50).map((r, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #edf2f7' }}>
                      <td style={{ padding: '4px', whiteSpace: 'nowrap' }}>{r.id || i + 1}</td>
                      <td style={{ padding: '4px' }}>{r.category || ''}</td>
                      <td style={{ padding: '4px' }}>{String(r.prompt || '').slice(0, 140)}</td>
                    </tr>))}</tbody>
                </table>
              </div>
              <div style={{ fontSize: '11px', color: '#718096', marginBottom: '8px' }}>
                Re-importing the same suite with the same IDs updates those prompts instead of duplicating them.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={doImport} disabled={busy} style={btn('#28a745')}>{busy ? 'Importing…' : `Import ${preview.valid} prompts`}</button>
                <button onClick={() => { setPreview(null); setPayload(null); setMsg(''); }} disabled={busy} style={btn('#6c757d')}>Cancel</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
